import type { CSSProperties, FC, ReactNode } from 'react'
import type { ProjectSummary } from '@/types'

import { AtlasMap } from '@/components/atlas/atlas-map'
import { FigLabel } from '@/components/generals/fig-label'
import { Reveal } from '@/components/generals/reveal'
import { TextLink } from '@/components/generals/text-link'

import { OFFICES } from '@/constants/navigation'

interface AtlasProps {
    projects: Array<ProjectSummary>
}

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

/**
 * Home section 10: the dotted map of Europe and the USA with both offices and every mapped project.
 * The full page at /atlas adds filters and a rotating cloud in the facts panel.
 */
export const Atlas: FC<AtlasProps> = ({ projects }): ReactNode => {
    const mapped = projects.filter(project => project.lat !== null && project.lon !== null).length

    return (
        <section id='atlas' aria-labelledby='atlas-title' className='relative border-t border-hairline'>
            <Reveal className='grid gap-8 px-gutter pb-12 pt-20 desk:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] desk:pb-16 desk:pt-28'>
                <div>
                    <FigLabel fig='10' title='Atlas' detail='Where the models live' note={`${OFFICES.length} offices / ${mapped} buildings / dashed lines lead from each office`} />

                    <h2 id='atlas-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                Three offices,
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                one Atlantic arc.
                            </span>
                        </span>
                    </h2>
                </div>

                <div className='reveal-fade self-end desk:justify-self-end' style={delay(260)}>
                    <p className='body-copy text-ink/85'>
                        Stockholm leads the work around the Baltic and the North Atlantic, Lisbon the Atlantic coast and New York the projects across the USA. Switch between Europe and the USA, or select a marker to fly to it and read its facts.
                    </p>

                    <TextLink href='/atlas' className='mt-6'>
                        Open the full atlas
                    </TextLink>
                </div>
            </Reveal>

            <div className='border-t border-hairline'>
                <AtlasMap
                    projects={projects}
                    idPrefix='home-atlas'
                    stageClassName='aspect-square sm:aspect-[4/3] desk:aspect-auto desk:h-[min(78svh,760px)] desk:min-h-[560px]'
                />
            </div>
        </section>
    )
}
